import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import JsBarcode from 'jsbarcode';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  printContainer: HTMLDivElement;

  constructor(private titleService: Title) {
    this.printContainer = document.createElement('div');
    this.printContainer.className = 'print-container container-fluid';
    window.document.body.appendChild(this.printContainer);
  }

  hasContent(): boolean {
    return this.printContainer.innerHTML != '';
  }

  reset() {
    this.printContainer.innerHTML = '';
  }

  addHeader(title: string, useLogo: boolean = true): Promise<void> {
    let _h = document.createElement('header');
    this.printContainer.appendChild(_h);
    return new Promise((resolve, reject) => {
      if (useLogo) {
        let _logo = new Image();
        _logo.onload = (e: any) => {
          _h.appendChild(_logo);
          if (title != '') {
            let _t = document.createElement('h1');
            _t.innerHTML = title;
            _h.appendChild(_t);
          }
          resolve();
        };
        _logo.src = 'assets/img/castorama-print.jpg';
        _logo.width = 305;
        _logo.height = 39;
      } else {
        let brand = document.createElement('h1');
        brand.innerHTML = 'Castorama';
        _h.appendChild(brand);
        if (title != '') {
          let _t = document.createElement('h1');
          _t.innerHTML = title;
          _h.appendChild(_t);
        }
        resolve();
      }
    });
  }

  addImage(src: string): Promise<void> {
    let _i = new Image();
    _i.className = 'img-fluid';
    this.printContainer.appendChild(_i);

    return new Promise((resolve, reject) => {
      _i.onload = (e: any) => {
        resolve();
      };
      _i.src = src;
    });
  }

  addPageBreak() {
    let _pb = document.createElement('div');
    _pb.className = 'page-break-after';
    this.printContainer.appendChild(_pb);
  }

  addText(text: string, style: string) {
    const _p = document.createElement('p');
    _p.innerHTML = text ?? '';
    _p.className = style ?? '';
    this.printContainer.appendChild(_p);
  }

  addDiv(div: HTMLDivElement): Promise<void> {
    if (!div) return Promise.resolve();

    this.printContainer.appendChild(div);
    return new Promise((resolve, reject) => {
      let _imgs = div.querySelectorAll('img');

      if (_imgs.length > 0) {
        let _imgToLoadCount = _imgs.length;
        let _imgLoaded = 0;

        for (let i = 0; i < _imgs.length; i++) {
          const _img: HTMLImageElement = _imgs[i];

          if (!_img.complete) {
            _img.onload = (e: any) => {
              _imgLoaded++;
              if (_imgLoaded === _imgToLoadCount) resolve();
            };
            _img.onerror = (e: any) => {
              _imgLoaded++;
              if (_imgLoaded === _imgToLoadCount) resolve();
            };
          } else {
            _imgLoaded++;
            if (_imgLoaded === _imgToLoadCount) resolve();
          }
        }
      } else {
        resolve();
      }
    });
  }

  addTable(table: HTMLTableElement): Promise<void> {
    this.printContainer.appendChild(table);
    return new Promise((resolve, reject) => {
      let _imgs = table.querySelectorAll('img');

      if (_imgs.length > 0) {
        let _imgToLoadCount = _imgs.length;
        let _imgLoaded = 0;

        for (let i = 0; i < _imgs.length; i++) {
          const _img: HTMLImageElement = _imgs[i];

          if (!_img.complete) {
            _img.onload = (e: any) => {
              _imgLoaded++;
              if (_imgLoaded === _imgToLoadCount) resolve();
            };
            _img.onerror = (e: any) => {
              _imgLoaded++;
              if (_imgLoaded === _imgToLoadCount) resolve();
            };
          } else {
            _imgLoaded++;
            if (_imgLoaded === _imgToLoadCount) resolve();
          }
        }
      } else {
        resolve();
      }
    });
  }

  print(specificName: string = undefined) {
    let oldTitle = this.titleService.getTitle();
    if (specificName != undefined) {
      this.titleService.setTitle(specificName);
    }
    window.print();
    if (specificName != undefined) {
      this.titleService.setTitle(oldTitle);
    }
  }

  printPdf(imgBase64Pdf: string, useIframe: boolean) {
    const byteCharacters = atob(imgBase64Pdf);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const file = new Blob([byteArray], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(file);

    if (useIframe) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = fileURL;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.print();
      };
    } else if (!window.open(fileURL)) {
      alert('Veuillez autoriser les pop-ups pour imprimer le PDF.');
    }
  }

  createBarCode(value: number | string): string {
    const xmlSerializer = new XMLSerializer();
    const svgNode = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    JsBarcode(svgNode, `${value}`, {
      xmlDocument: document,
      height: 50,
      width: 2.3,
      text: '' + value,
      font: 'Casto Pro Bold',
      fontOptions: 'bold',
      fontSize: 25,
      lineColor: '#000000',
      margin: 20,
      textMargin: 5,
      textAlign: 'center',
      textPosition: 'top',
    });

    return xmlSerializer.serializeToString(svgNode);
  }

  printBarcode(title: string, value: number | string, img?: string) {
    return new Promise(() => {
      setTimeout(() => {
        let popupWin = window.open('', '_blank', 'top=0,left=0,height=0,width=auto');
        popupWin.document.open();
        popupWin.document.write(`
                  <html>
                    <head>
                    <style scoped>
                    .print-container {
                      display: block;
                      text-align: center;
                      background-color: white;
                      border: 1px solid $light-blue;
                      border-radius: $border-radius;
                      padding: 1.5em;
                    }
                    .no-print {
                      text-align: center;
                      align-items: center;
                      width: 100vw;
                      height:100vh;
                    }
                    .heading-print {
                      font-family: 'Casto Pro Bold';
                      background-color:#e6e6e6;
                      font-size: 30px;
                      font-weight: bold;
                      color: #20407f;
                    }
                    .display-tbl {
                      border-collapse: collapse;
                      table-layout: fixed;
                      width: 100%;
                      height:100%;
                      .tr {
                        align-items: center;
                        width: 100%;
                        .td {
                          text-align: center;
                          width: 100%;
                        }
                      }
                    }
                   .print{
                      display: none;
                    }
                    @media print {
                        .print {
                          display: block;
                        }
                        .no-print {
                          display: none;
                        }
                    }
                  </style>
                    </head>
                    <body>
                      <div class="no-print heading-print">
                        <table class="display-tbl">
                        <tr>
                           <td><h1 style="text-align: center; font-size: 30px; font-weight: bold;">Cliquer/Toucher pour fermer</h1></td>
                        </tr>
                        </table>
                      </div>
                      <div class="print print-container">
                        <h1 class="bg-blue">${title}</h1>
                        ${img ? `<img class="img-fluid" src="${img}">` : ''}
                        <div>
                          ${this.createBarCode(value)}
                        </div>
                      </div>
                      <script defer>
                        function triggerPrint(event) {
                          window.removeEventListener('load', triggerPrint, false);
                          setTimeout(function() {
                            closeWindow(window.print());
                          }, ${1000});
                        }
                        function closeWindow(){}
                        function winclose(){
                          window.close();
                        };
                        window.document.body.addEventListener('click', winclose, true);
                        window.document.body.addEventListener('touchstart', winclose, true);
                        window.document.body.addEventListener('touchmove', winclose, true);
                        window.document.body.addEventListener('touchcancel', winclose, true);
                        window.addEventListener('load', triggerPrint, false);
                      </script>
                    </body>
                  </html>`);
        popupWin.document.close();
      }, 0);
    });
  }
}
